import React, { useEffect, useMemo, useState } from 'react';
import { DisbursementContainer } from './DisbursementContainer';
import { useDisbursementTypes } from './useDisbursementTypes';
import { ICreateSettlementFeeForm } from 'types/order/fees/ICreateSettlementItemForm';
import { IDisbursementFormData } from 'types/order/escrow/IDisbursementFormData';
import { ButtonDropdown } from 'ui-toolkit';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { useHistory } from 'react-router-dom';
import { IOrderDetailsData } from '../../../../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { ErrorBoundaryConstellation } from '../../../../../constellationExtended/ErrorBoundary/ErrorBoundaryConstellation';
import { useHasFeatureFlag } from '../../../../../selectors/useHasFeatureFlag';

export interface IDisbursementsProps {
  fee: ICreateSettlementFeeForm;
  order: IOrderDetailsData;
  list: IDisbursementFormData[];
  feeName: string;
}

const removeSearchParams = (history: any, params: any) => {
  if (params.has('statementFeeId') || params.has('create')) {
    if (params.has('statementFeeId')) {
      params.delete('statementFeeId');
    }
    if (params.has('create')) {
      params.delete('create');
    }
    history.replace({
      search: params.toString(),
    });
  }
};

export const Disbursements: React.FC<IDisbursementsProps> = ({ list, fee, order, feeName }) => {
  const history = useHistory();
  const urlSearchString = window.location.search;
  const params = useMemo(() => new URLSearchParams(urlSearchString), [urlSearchString]);
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');
  const isZillowEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const [newList, setNewList] = useState<IDisbursementFormData[]>([]);
  const disbursementTypes = useDisbursementTypes();
  const disbursementOptions = disbursementTypes.filter((t) => t.hasEditPermission);
  const newDisbursementOptions = disbursementOptions.map((t) => ({
    children: t.type,
    onClick: () => setNewList((value) => value.concat([t.create(fee, order, isSpruceWithZillowEnabled, isZillowEnabled)])),
  }));
  const checkType = disbursementOptions.find((t) => t.type === eDisbursementType.Check)!;
  if (checkType) {
    newDisbursementOptions.push({
      children: 'Holdback',
      onClick: () => {
        const newHoldback = {
          ...checkType.create(fee, order, isSpruceWithZillowEnabled, isZillowEnabled),
          holdback: true,
        };
        setNewList((t) => t.concat(newHoldback));
      },
    } as any);
  }

  useEffect(() => {
    if (newList.length === 0 && params.get('statementFeeId') === fee.statementFeeId && list.length === 0) {
      const newDisbursementType = disbursementOptions.find((o) => o.type === params.get('create'));
      if (newDisbursementType) setNewList([newDisbursementType.create(fee, order, isSpruceWithZillowEnabled, isZillowEnabled)]);
      removeSearchParams(history, params);
    }
  }, [newList, params, fee, history, list, disbursementOptions, setNewList, order]);

  return (
    <ErrorBoundaryConstellation>
      {list.map((item) => (
        <DisbursementContainer
          key={item.id}
          disbursement={item}
          disbursements={list}
          disbursementTypes={disbursementTypes}
          fee={fee}
          feeName={feeName}
          order={order}
          onPush={(value) => setNewList([...newList, value])}
        />
      ))}
      {newList.map((item) => (
        <DisbursementContainer
          key={item.id}
          disbursement={item}
          disbursements={list}
          disbursementTypes={disbursementTypes}
          fee={fee}
          feeName={feeName}
          order={order}
          onRemove={() => setNewList((oldList) => [...oldList.filter((t) => t.id !== item.id)])}
          onPush={(value) => setNewList((oldList) => [...oldList, value])}
        />
      ))}
      {fee.statementFeeId && newDisbursementOptions.length > 0 && (
        <div className={'flex flex-row gap-1'}>
          <ButtonDropdown intent={'primary'} items={newDisbursementOptions} data-test-id={'add-new-disbursement'}>
            + New Disbursement
          </ButtonDropdown>
        </div>
      )}
    </ErrorBoundaryConstellation>
  );
};
